import React, { useContext, useEffect, useRef, useState } from "react"
import { useInterval } from "../hooks/useInterval"
import { ICurrentUser, useGlobalState } from "./GlobalStateProvider"
import { batch } from "../../server/utils/arrays"

export const PollsContext = React.createContext<IPollContext | null>(null)

interface IPollContext {
  registerFetch(name: string, fetch: any): any

  unregisterFetch(name: string): void

  getResult(name: string): any
}

interface IProps {
  children: React.ReactNode
}

export function PollsProvider({ children }: IProps) {
  const fetchesRef = useRef<any>({})
  const skeem = window.skeem
  const [result, setResult] = useState<any>({})

  useInterval(() => fetch(), 20 * 1000)

  function handleRegisterFetch(name: string, fetch: any) {
    fetchesRef.current = { ...fetchesRef.current, [name]: fetch }
  }

  function handleUnregisterFetch(name: string) {
    fetchesRef.current = { ...fetchesRef.current, [name]: undefined }
  }

  useEffect(() => {
    setTimeout(() => {
      fetch()
    }, 2000)
  }, [])

  async function fetch() {
    if (Object.keys(fetchesRef.current).length > 0) {
      const groups = batch(Object.keys(fetchesRef.current), 19)
      const resultData = await Promise.all(
        groups.map(group =>
          skeem.fetch2<any>({
            obj: group.reduce((fetchQuery, key) => {
              fetchQuery[key] = fetchesRef.current[key]
              return fetchQuery
            }, {} as any),
          }),
        ),
      )
      const result = resultData.reduce((data, result) => ({ ...data, ...result }), {} as any)
      setResult(result)
    }
  }

  function getResult(name: string) {
    return result[name]
  }

  return (
    <PollsContext.Provider
      value={{
        registerFetch: handleRegisterFetch,
        unregisterFetch: handleUnregisterFetch,
        getResult,
      }}
    >
      {children}
    </PollsContext.Provider>
  )
}

export function usePoll() {
  const value = useContext(PollsContext)
  if (!value) {
    throw new Error("PollsProvider must be used within a PollsContext.")
  }
  return value
}

export function usePollingFetch<T extends any>(
  fetchQuery: Record<string, any>,
  cb?: (result: T) => void,
) {
  const str = useRef((Math.random() + 1).toString(36).substring(7))
  const ctx = usePoll()

  useEffect(() => {
    ctx.registerFetch(str.current, fetchQuery)

    return () => ctx.unregisterFetch(str.current)
  }, [])

  const res = ctx.getResult(str.current)

  useEffect(() => {
    if (cb) {
      cb(res && res)
    }
  }, [JSON.stringify(res)])

  return res as T
}

export function usePollingFetchWithCurrentUser<T extends any>(
  fetchQuery: (user: ICurrentUser) => Record<string, any>,
  cb?: (result: any) => void,
) {
  const { currentUser } = useGlobalState()
  const str = useRef((Math.random() + 1).toString(36).substring(7))
  const ctx = usePoll()

  useEffect(() => {
    if (currentUser?.id) {
      ctx.registerFetch(str.current, fetchQuery(currentUser))
    }

    return () => ctx.unregisterFetch(str.current)
  }, [])

  const res = ctx.getResult(str.current)

  useEffect(() => {
    if (cb && res) {
      cb(res)
    }
  }, [JSON.stringify(res)])

  return res as T
}
